namespace Solovis.Reports.Parts.Tables.DataTableHelpers {

  export type ColumnDefinition = {
    label: string;
    superHeaders: Record<string, any>;
    width?: number;
    textAlign: string;
    headerAlign?: string;
    noWrap?: boolean;
    formatHint?: { scale?: number };
    applyStandardGrouping?: boolean;
    // aggregation should have an aggregator property with value of "sum" or other supported aggregation method.
    aggregation?: Record<string, any>;
  } & Record<string, any>; // record is placeholder here till we pull type for _col

  export class Column {

    public columnSet: ColumnSet;

    public _col;

    public definition;
    public id;
    public field;
    public dataType;
    public label;
    public superHeaders;
    public hiddenByDefault;
    private _isEditable: boolean | ((row: any) => boolean);

    public mods;

    public definitionToUse?: ColumnDefinition;


    constructor(options) {
      this.columnSet = options.columnSet;

      this._col = options.column ? options.column : null;
      if (!this._col) {
        throw "column not defined";
      }

      this.definition = this._col;

      this.id = this._col.id;
      this.field = this._col.field ? this._col.field : null;
      this.dataType = this._col.dataType ? this._col.dataType : null;
      this.label = this._col.label;
      this.superHeaders = this._col.superHeaders ? this._col.superHeaders : null;
      this.hiddenByDefault = this._col.hiddenByDefault ? this._col.hiddenByDefault : false;
      this._isEditable = this._col.isEditable ?? false;

      this.mods = options.mods ? options.mods : null;

      this._updateDefinitionToUse();
    }


    /**
     * Returns true if this column accepts user edits for the given row. Supports both a boolean
     * `isEditable` flag and a function that can decide per-row. Grouping/aggregate rows are
     * never editable — only leaf data rows.
     */
    public isEditableForRow(row: any): boolean {
      return Column.isColEditableForRow(this._col, row);
    }

    /**
     * Tests whether the given column definition allows editing for the given row. Static so the
     * renderer can call it with the plain definition object without needing to find the Column
     * instance.
     */
    public static isColEditableForRow(col: any, row: any): boolean {
      const editable = col?.isEditable;
      if (!editable || !row || Column.isGroupingRow(row)) {
        return false;
      }
      if (typeof editable === "function") {
        return !!editable(row);
      }
      return true;
    }

    /**
     * A grouping/aggregate row has child rows (_rows) or aggregated totals (_groupTotals) attached.
     */
    public static isGroupingRow(row: any): boolean {
      return !!(row && (row._rows || row._groupTotals));
    }


    /**
     * updateMods changes the modifications applied to a column definition.
     * @param mods
     * @param suppressColumnChangedEvent
     */
    public updateMods(mods, suppressColumnChangedEvent?) {
      this.mods = mods;
      this._updateDefinitionToUse();

      if (!suppressColumnChangedEvent) {
        this.columnSet.notifyColumnsChanged({
          columnId: this.id,
          modsUpdated: true
        });
      }
    }

    public _updateDefinitionToUse() {
      const definitionToUse: Record<string, any> = {};

      for (const key in this.definition) {
        let value = this.definition[key];

        if (key === "formatHint") {
          // We deepCopy formatHint since we will change nested properties on that with mods.
          value = Solovis.deepCopy(value);
        }

        definitionToUse[key] = value;
      }

      // We whitelist the available mods below.
      if (this.mods) {
        if (Solovis.isNotNull(this.mods.label)) {
          definitionToUse.label = this.mods.label;
        }

        if (Solovis.isNotNull(this.mods.superHeaders)) {
          definitionToUse.superHeaders = Solovis.deepCopy(this.mods.superHeaders);
        }

        if (Solovis.isNotNull(this.mods.width)) {
          definitionToUse.width = this.mods.width;
        }

        if (Solovis.isNotNull(this.mods.textAlign)) {
          definitionToUse.textAlign = this.mods.textAlign;
        }

        if (Solovis.isNotNull(this.mods.headerAlign)) {
          definitionToUse.headerAlign = this.mods.headerAlign;
        }

        if (Solovis.isNotNull(this.mods.noWrap)) {
          definitionToUse.noWrap = this.mods.noWrap;
        }

        if (this.mods.formatHint) {
          if (!definitionToUse.formatHint) {
            definitionToUse.formatHint = {};
          }

          if (Solovis.isNotNull(this.mods.formatHint.scale)) {
            definitionToUse.formatHint.scale = parseInt(this.mods.formatHint.scale);
          }
        }

        if (Solovis.isNotNull(this.mods.applyStandardGrouping)) {
          definitionToUse.applyStandardGrouping = this.mods.applyStandardGrouping;
        }

        if (this.mods.aggregation) {
          // aggregation should have an aggregator property with value of "sum" or other supported aggregation method.
          definitionToUse.aggregation = Solovis.deepCopy(this.mods.aggregation);

          // We apply a custom htmlFormatter wrapper to handle counting aggregator.
          var aggregator = definitionToUse.aggregation.aggregator;
          if (aggregator === "count" || aggregator === "countIfNotNull" || aggregator === "uniqueCount" || aggregator === "uniqueCountIfNotNull") {
            var baseFormatter = definitionToUse.htmlFormatter;
            definitionToUse.htmlFormatter = function (value, valueContext) {
              if (valueContext.aggregate) {
                // Show the count as an integer.
                if (typeof value == "number") {
                  return value.toString();
                }
                else {
                  return null;
                }
              }

              if (baseFormatter) {
                return baseFormatter(value, valueContext);
              }
              else {
                return valueContext.defaultFormatter(value, valueContext);
              }
            };
          }
        }
      }

      // See if we need to apply a default for text-alignment.
      if (Solovis.isNull(definitionToUse.textAlign)) {
        var dataType = this.dataType;
        if (dataType == "dayId" || dataType == "bool" || dataType == "currencyId") {
          definitionToUse.textAlign = "center";
        }
        else if (dataType == "number" || dataType == "currency" || dataType == "integer" || dataType == "percentage" || dataType == "hybridReturn") {
          definitionToUse.textAlign = "right";
        }
      }

      // See if we need to apply a default for width.
      if (Solovis.isNull(definitionToUse.width)) {
        if (dataType == "dayId") {
          definitionToUse.width = 105;
        }
        else if (dataType == "currencyId") {
          definitionToUse.width = 65;
        }
        else if (dataType == "percentage") {
          definitionToUse.width = 75;
        }
        else if (dataType == "hybridReturn") {
          definitionToUse.width = 130;
        }
        else if (dataType == "currency" || dataType == "number") {
          definitionToUse.width = 115;
        }
        else if (dataType == "integer") {
          definitionToUse.width = 90;
        }
        else if (dataType == "utcTimestamp") {
          definitionToUse.width = 175;
        }
        else {
          definitionToUse.width = 150;
        }
      }

      this.definitionToUse = definitionToUse as ColumnDefinition;
    }

    public getCompositeLabel() {
      const colDef = this.definitionToUse;

      let label = colDef?.label;
      if (colDef?.superHeaders && colDef.superHeaders.length > 0) {
        for (var i = 0; i < colDef.superHeaders.length; i++) {
          label = colDef.superHeaders[i] + " : " + label;
        }
      }

      return label;
    }
  }

}
